import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/buttons/copy-button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { LinkText } from '@/components/ui/link-text';
import { Text } from '@/components/ui/text';
import { TimePicker } from '@/components/ui/time-picker';
import { Switch } from '@/components/ui/toggle';
import { DialogForm } from '@/features/dialog/dialog-form';
import { CustomerSelect } from '@/features/shared/components/form-items-with-fetch/customer-select';
import { getFullAddressString } from '@/lib/address-helper';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CustomerDTO } from '@/server/dto/customer-dto';
import type { EventInput } from '@/server/queries/utils/crm-event-build-input';
import { getDefaultRecurrence } from '@/utils/rrule';
import {
    addDays,
    addMinutes,
    differenceInMinutes,
    format,
    formatISO,
    min,
    parse,
    parseISO,
    startOfToday,
} from 'date-fns';
import {
    CalendarIcon,
    ClockIcon,
    MapPinIcon,
    MinusIcon,
    StoreIcon,
    TypeIcon,
    UserCircleIcon,
    XIcon,
} from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { RRuleInput } from './rrule-input';

export type EventFormData = EventInput;

export interface EventFormFieldsProps {
    form: UseFormReturn<EventFormData, undefined>;
    customer?: CustomerDTO;
    includeLinks?: boolean;
    ownerName: string;
}

const DEFAULT_EVENT_LENGTH_MINUTES = 30;

const parseTime = (day: Date, time: string | undefined | null) => {
    return time ? parse(time, 'HH:mm:ss', day) : undefined;
};

const formatTime = (date: Date) => {
    return format(date, 'HH:mm:ss');
};

const getMinEndTime = (startTime: string, referenceDate: Date) => {
    const start = parse(startTime, 'HH:mm:ss', referenceDate);
    return formatTime(addMinutes(start, 15));
};

export const EventFormFields = ({
    form,
    customer,
    ownerName,
    includeLinks = false,
}: EventFormFieldsProps) => {
    const { t } = useLocale('events');
    const {
        control,
        formState: { errors, disabled },
        setValue,
        watch,
        getValues,
    } = form;

    const getGoogleMapsUrl = (address: string) => {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    };

    const date = watch('date');
    const currentDay = date ? parseISO(date) : startOfToday();
    const startTime = watch('start_time');

    return (
        <DialogForm.ItemList>
            {/* Description */}
            <DialogForm.Item className="gap-1">
                <DialogForm.Label htmlFor="description" icon={TypeIcon} />
                <DialogForm.Body>
                    <Controller
                        control={control}
                        name="description"
                        render={({ field }) => (
                            <Input
                                variant="inline"
                                placeholder={t('events__form_title_placeholder')}
                                {...field}
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.description} />
            </DialogForm.Item>

            {/* Customer */}
            <DialogForm.Item className="items-start gap-1">
                <DialogForm.Label htmlFor="customer_id" icon={StoreIcon} />
                <DialogForm.Body className="flex flex-col gap-2">
                    <Controller
                        name="customer_id"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: t('form__customer_required', { ns: 'common' }),
                            },
                        }}
                        render={({ field: { value, onChange, disabled } }) => (
                            <CustomerSelect
                                className="w-full"
                                variant="inline"
                                value={value}
                                onChange={(customerId, customer) => {
                                    onChange(customerId);
                                    setValue(
                                        'address',
                                        customer
                                            ? getFullAddressString({
                                                  street: customer.street,
                                                  zipCode: customer.zip_code,
                                                  city: customer.city,
                                                  countryCode: customer.country_code,
                                              })
                                            : '',
                                        {
                                            shouldValidate: true,
                                        },
                                    );
                                }}
                                customer={customer}
                                includeLink={includeLinks}
                                error={!!errors.customer_id}
                                disabled={disabled}
                            />
                        )}
                    />
                    <DialogForm.Error error={errors.customer_id} />
                </DialogForm.Body>
            </DialogForm.Item>

            {/* Address */}
            <DialogForm.Item className="items-start gap-1">
                <DialogForm.Label icon={MapPinIcon} />
                <DialogForm.Body>
                    <Controller
                        name="address"
                        control={control}
                        render={({ field }) => (
                            <div className="flex flex-col gap-2 w-full">
                                <div className="flex flex-row gap-2">
                                    <Input
                                        placeholder={t('events__form_address_placeholder')}
                                        variant="inline"
                                        {...field}
                                        disabled
                                        className="flex-1 min-w-0"
                                        title={field.value ?? ''}
                                    />
                                    {includeLinks && field.value && (
                                        <CopyButton title="" textToCopy={field.value} />
                                    )}
                                </div>
                                {includeLinks && field.value && (
                                    <div className="px-2">
                                        <LinkText
                                            text={t('events__open_in_maps')}
                                            href={getGoogleMapsUrl(field.value)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-blue-600"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.address} />
            </DialogForm.Item>

            {/* Event Date */}
            <DialogForm.Item className="items-start gap-0">
                <DialogForm.Label icon={CalendarIcon} />
                <DialogForm.Body className="flex-col gap-2">
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 flex-col sm:flex-row sm:items-center">
                            <Controller
                                name="date"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        disableIcon
                                        mode="single"
                                        value={field.value ? new Date(field.value) : new Date()}
                                        valuePrefix=""
                                        onChange={(value) => {
                                            field.onChange(
                                                formatISO(value ?? new Date(), {
                                                    representation: 'date',
                                                }),
                                            );
                                        }}
                                        disabled={field.disabled}
                                    />
                                )}
                            />
                            <Controller
                                name="recurrence"
                                control={control}
                                render={({ field }) => (
                                    <div className="px-3">
                                        <Switch
                                            checked={!!field.value}
                                            onCheckedChange={(value) => {
                                                field.onChange(value ? getDefaultRecurrence() : '');
                                            }}
                                            size="sm"
                                            label={t('events__form_regular_event_label')}
                                            disabled={field.disabled}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                        <Controller
                            name="recurrence"
                            control={control}
                            render={({ field }) => (
                                <>
                                    {!!field.value && (
                                        <div className="pl-3">
                                            <RRuleInput startDate={watch('date')} {...field} />
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                </DialogForm.Body>
                <DialogForm.Error error={errors.date} />
            </DialogForm.Item>

            {/* Time Range */}
            <DialogForm.Item className="items-center flex-nowrap gap-0">
                <DialogForm.Label icon={ClockIcon} />
                <DialogForm.Body className="flex-row items-center gap-1">
                    <Controller
                        name="start_time"
                        control={control}
                        render={({ field }) => (
                            <TimePicker
                                value={field.value ?? null}
                                onChange={(value) => {
                                    const currentDay = parseISO(getValues('date'));
                                    const nextDay = addDays(currentDay, 1);
                                    const startTime = parseTime(currentDay, field.value);
                                    const endTime = parseTime(
                                        getValues('end_time') === '00:00:00' ? nextDay : currentDay,
                                        getValues('end_time'),
                                    );

                                    field.onChange(value);

                                    const nextStartTime = parseTime(currentDay, value);

                                    if (!nextStartTime) {
                                        setValue('end_time', undefined);
                                        return;
                                    }

                                    const eventLength =
                                        startTime && endTime
                                            ? differenceInMinutes(endTime, startTime)
                                            : DEFAULT_EVENT_LENGTH_MINUTES;
                                    const nextEndTime = min([
                                        addMinutes(nextStartTime, eventLength),
                                        nextDay,
                                    ]);

                                    setValue('end_time', formatTime(nextEndTime));
                                }}
                                placeholder={t('events__form_time_start_placeholder')}
                                disabled={field.disabled}
                            />
                        )}
                    />
                    <MinusIcon className="text-sm text-neutral-400" />
                    <Controller
                        name="end_time"
                        control={control}
                        render={({ field }) => (
                            <TimePicker
                                disabled={field.disabled || !startTime}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                placeholder={t('events__form_time_end_placeholder')}
                                min={startTime ? getMinEndTime(startTime, currentDay) : undefined}
                            />
                        )}
                    />
                    {!!startTime && !disabled && (
                        <Button
                            variant="ghost"
                            icon={XIcon}
                            size="icon"
                            className="p-0"
                            onClick={() => {
                                form.setValue('end_time', undefined);
                                form.setValue('start_time', undefined);
                            }}
                        />
                    )}
                </DialogForm.Body>
                <DialogForm.Error error={errors.start_time || errors.end_time} />
            </DialogForm.Item>

            {/* Created By (Display Only) */}
            <DialogForm.Item>
                <DialogForm.Label icon={UserCircleIcon} />
                <DialogForm.Body className="items-center">
                    <Text color="neutral-950">{t('events__form_created_by', { ownerName })}</Text>
                </DialogForm.Body>
            </DialogForm.Item>
        </DialogForm.ItemList>
    );
};
